import { useEffect, useState } from "react";

import { Feature } from "common/types";

import { useControlledSubstanceGuidanceDrawer } from "client/controlledSubstances/stackables";
import { useControlledSubstancesSummaryMutation } from "client/carts/hooks";
import { ControlledSubstancesSummary } from "client/carts/types";
import { useFeatures } from "client/clinics/hooks";
import TextChip from "client/common/components/content/TextChip";
import TextButton from "client/common/components/buttons/TextButton";

import ProductLineReviews from "./ProductLineReviews";
import { IProductLineDetails } from "../types";

import styles from "../styles/ProductLineInfo.module.less";

type ProductLineInfoProps = {
  productLine: IProductLineDetails;
  onReviewClick: () => void;
};

const ProductLineInfo = ({ productLine, onReviewClick }: ProductLineInfoProps) => {
  const features = useFeatures();
  const isCarouselV2 = features[Feature.CarouselV2];
  const { open: openControlledSubstanceGuidanceDrawer } = useControlledSubstanceGuidanceDrawer();

  const [controlledSubsSummary, setControlledSubsSummary] = useState<
    ControlledSubstancesSummary | undefined
  >(undefined);
  const { mutate: getControlledSubsInfo } = useControlledSubstancesSummaryMutation({
    onSuccess: setControlledSubsSummary,
  });

  useEffect(() => {
    if (!productLine.products.length || !productLine.products[0].items.length) {
      return;
    }
    getControlledSubsInfo({
      address_id: undefined,
      // Controlled substances exist at the ProductLine level,
      // so we can use the first Item in the ProductLine
      item_ids: [productLine.products[0].items[0].id],
    });
  }, [getControlledSubsInfo, productLine.products]);

  return (
    <div className={styles.container}>
      {(isCarouselV2 ? productLine.is_featured_v2 : !!productLine.featured_categories.length) && (
        <div className={styles.tags}>
          <TextChip variant="primary">
            {isCarouselV2 ? (
              "Your Vet's Choice"
            ) : (
              <>
                Your Vet&apos;s Choice for{" "}
                {productLine.featured_categories.map((c) => c.extended_name).join(",")}
              </>
            )}
          </TextChip>
        </div>
      )}
      <h1 className={styles.title}>{productLine.name}</h1>
      <ProductLineReviews.Widget productLine={productLine} onClick={onReviewClick} />
      {productLine.short_description && (
        <p className={styles.description}>{productLine.short_description}</p>
      )}
      {controlledSubsSummary?.is_controlled && (
        <TextChip variant="grey">
          <TextButton onClick={openControlledSubstanceGuidanceDrawer}>
            Controlled substance
          </TextButton>
        </TextChip>
      )}
    </div>
  );
};

export default ProductLineInfo;
